import { RecurrenceRule } from '../components/calendar/dialogs/RecurrenceSelector';

export interface RecurrenceInstance {
  dates: string[];
  parentEventId?: string;
  instanceDate: string; // Original date of this instance
}

export function generateRecurrenceInstances(
  startDate: string,
  endDate: string,
  rule: RecurrenceRule,
  limit: number = 365
): RecurrenceInstance[] {
  if (rule.frequency === 'none') return [];
  
  const instances: RecurrenceInstance[] = [];
  const start = new Date(startDate + 'T12:00:00'); // Add time to avoid timezone issues
  const end = new Date(endDate + 'T12:00:00');
  const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  
  let current = new Date(start);
  let count = 0;
  
  // Calculate end limit
  let endLimit: Date | null = null;
  if (rule.endDate) {
    endLimit = new Date(rule.endDate + 'T23:59:59');
  } else if (!rule.occurrences) {
    // If no end date or occurrences, limit to 1 year from start
    endLimit = new Date(start);
    endLimit.setFullYear(endLimit.getFullYear() + 1);
  }
  
  // Start from the first recurrence (not the original event)
  if (rule.frequency === 'daily') {
    current.setDate(current.getDate() + rule.interval);
  } else if (rule.frequency === 'weekly') {
    current.setDate(current.getDate() + (7 * rule.interval));
  } else if (rule.frequency === 'monthly') {
    current.setMonth(current.getMonth() + rule.interval);
  } else if (rule.frequency === 'yearly') {
    current.setFullYear(current.getFullYear() + rule.interval);
  }
  
  while (count < limit - 1) { // -1 because we don't include the original
    // Check if we've exceeded limits
    if (endLimit && current > endLimit) break;
    if (rule.occurrences && count >= rule.occurrences - 1) break; // -1 for original
    
    // Generate dates for this instance
    const instanceDates: string[] = [];
    const instanceStart = new Date(current);
    
    for (let d = 0; d <= duration; d++) {
      const date = new Date(instanceStart);
      date.setDate(date.getDate() + d);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      instanceDates.push(dateKey);
    }
    
    instances.push({
      dates: instanceDates,
      instanceDate: instanceDates[0]
    });
    
    // Move to next occurrence
    if (rule.frequency === 'daily') {
      current.setDate(current.getDate() + rule.interval);
    } else if (rule.frequency === 'weekly') {
      current.setDate(current.getDate() + (7 * rule.interval));
    } else if (rule.frequency === 'monthly') {
      const originalDay = start.getDate();
      current.setMonth(current.getMonth() + rule.interval);
      // Handle month end edge cases
      if (current.getDate() !== originalDay) {
        current.setDate(0); // Go to last day of previous month
      }
    } else if (rule.frequency === 'yearly') {
      current.setFullYear(current.getFullYear() + rule.interval);
    }
    
    count++;
  }
  
  return instances;
}